import { useParams, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { services } from "@/data/services";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Globe, Router, Network, Wifi, Phone, PhoneCall, Cloud, Users, Smartphone, Monitor, Package, Cpu, Shield, Mail, HardDrive, RefreshCw, CheckCircle2, Camera, Laptop, Recycle, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import InternetContent from "@/components/services/InternetContent";
import NetworkContent from "@/components/services/NetworkContent";
import TelefonieContent from "@/components/services/TelefonieContent";
import WerkplekContent from "@/components/services/WerkplekContent";
import { type LucideIcon } from "lucide-react";
import FAQ from "@/components/FAQ";
import { internetFAQ, telefonieFAQ, werkplekFAQ, buildFAQSchema } from "@/data/faqs";


// Per-item icon + gradient overrides, keyed by item name
const itemMeta: Record<string, { icon: LucideIcon; gradient: string; iconBg: string }> = {
  // Netwerken
  "Internet":         { icon: Globe,    gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Router / Firewall":{ icon: Router,   gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Switch":           { icon: Network,  gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  "Access Points":    { icon: Wifi,     gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  // Internet
  "Rinqel VDSL":          { icon: Globe,   gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Rinqel Glasvezel":     { icon: Globe,   gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Rinqel 5G Onbeperkt":  { icon: Wifi,    gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  "Rinqel 5G Backup":     { icon: Shield,  gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Rinqel met Starlink":  { icon: Cloud,   gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  // Telefonie
  "VoIP Centrale":              { icon: PhoneCall, gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "VoIP Aansluiting":           { icon: Phone,     gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Bellen met Teams":           { icon: Monitor,   gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  "Softphone / App bellen":     { icon: Smartphone,gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "VAMO – Vast-Mobiel Integratie": { icon: Smartphone, gradient: "bg-rinqel-blue-gradient", iconBg: "bg-secondary/10" },
  "CRM & ERP koppelingen":      { icon: HardDrive, gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  // Mobiel
  "Sim Only":                   { icon: Smartphone, gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Vast-mobiel integratie":     { icon: Phone,      gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Groepsbundels":              { icon: Users,      gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  "Data-only simkaarten":       { icon: Wifi,       gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  // Werkplek
  "Managed Cloud Only Werkplek": { icon: Cloud,      gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Managed Werkplek":            { icon: Monitor,    gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Managed Kiosk Werkplek":      { icon: Monitor,    gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  "Managed Mobile Device Only":  { icon: Smartphone, gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Losse Mailbox":               { icon: Mail,       gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Gedeelde Mailbox":            { icon: Users,      gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  // Software
  "Microsoft 365":              { icon: Monitor,    gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Adobe Creative Cloud":       { icon: Package,    gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Autodesk":                   { icon: Cpu,        gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  // Hardware
  "Laptops & Desktops":         { icon: Laptop,     gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Telefoons & Tablets":        { icon: Smartphone, gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Monitoren":                  { icon: Monitor,    gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
  "Printers & Scanners":        { icon: Package,    gradient: "bg-hero-gradient",         iconBg: "bg-primary/10" },
  "Netwerkapparatuur":          { icon: Network,    gradient: "bg-rinqel-blue-gradient",  iconBg: "bg-secondary/10" },
  "Camera's & Opname apparatuur": { icon: Camera,   gradient: "bg-rinqel-green-gradient", iconBg: "bg-rinqel-green/10" },
};

// Links to subpages for specific items
const itemLinks: Record<string, string> = {
  "Managed Cloud Only Werkplek": "/diensten/werkplek/cloud-only",
  "Managed Werkplek": "/diensten/werkplek/managed",
  "Managed Mobile Device Only": "/diensten/werkplek/mobile-device",
  "Managed Kiosk Werkplek": "/diensten/werkplek/kiosk",
  "Losse Mailbox": "/diensten/werkplek/exchange-online",
};

const ServicePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = services.find((s) => s.slug === slug);

  // Compute lowest internet price for use in netwerken items
  const internetService = services.find((s) => s.slug === "internet");
  const lowestInternetPrice = internetService
    ? internetService.items
        .map((item) => {
          const match = item.price?.match(/€(\d+)/);
          return match ? parseInt(match[1], 10) : Infinity;
        })
        .reduce((min, val) => Math.min(min, val), Infinity)
    : null;

  // Override the Internet item price in netwerken with the dynamic lowest price
  const displayItems =
    slug === "netwerken" && service && lowestInternetPrice !== null && lowestInternetPrice !== Infinity
      ? service.items.map((item) =>
          item.name === "Internet"
            ? { ...item, price: `Vanaf €${lowestInternetPrice}`, priceNote: "per maand" }
            : item
        )
      : service?.items ?? [];

  if (!service) {
    return (
      <Layout seoTitle="Dienst niet gevonden" noIndex>
        <div className="container py-24 text-center">
          <h1 className="text-2xl font-bold">Dienst niet gevonden</h1>
          <Button variant="hero-outline" className="mt-6" asChild>
            <Link to="/">Terug naar home</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const Icon = service.icon;

  // Hero style: full-width image with dark overlay for telefonie, subtle gradient for others
  const hasFullHero = !!service.image;

  // Per-slug Google/Schema.org-aligned Service metadata
  const serviceMeta: Record<string, { serviceType: string; category: string; description: string }> = {
    internet: {
      serviceType: "Business Internet Service Provider",
      category: "Telecommunications / Internet access",
      description:
        "Zakelijk internet van Rinqel: glasvezel, VDSL, 5G en Starlink met 4G/5G-backup, professionele router/firewall en proactief netwerkbeheer. Maandelijks opzegbaar, vaste prijs, altijd inclusief support.",
    },
    netwerken: {
      serviceType: "Managed Network Services",
      category: "Managed IT services / Networking",
      description:
        "Volledig beheerde bedrijfsnetwerken: router, firewall, switches en WiFi access points. Ontwerp, installatie, monitoring en updates door Rinqel — altijd snel en veilig.",
    },
    mobiel: {
      serviceType: "Mobile Network Operator Reseller",
      category: "Telecommunications / Mobile telephony",
      description:
        "Zakelijke mobiele abonnementen via het netwerk van KPN, Vodafone en Odido. Dagelijks aanpasbaar, maandelijks opzegbaar, met centraal beheer en één factuur.",
    },
    telefonie: {
      serviceType: "VoIP Telephony Service Provider",
      category: "Telecommunications / Cloud telephony",
      description:
        "Cloudtelefonie en VoIP voor bedrijven: telefooncentrale in de cloud, bellen met Microsoft Teams, softphone-app, vast-mobiel integratie (VAMO) en koppelingen met CRM en ERP.",
    },
    werkplek: {
      serviceType: "Managed Workplace Services (Microsoft 365)",
      category: "Managed IT services / Modern workplace",
      description:
        "Beheerde digitale werkplekken op basis van Microsoft 365: identiteit, apparaatbeheer met Intune, beveiliging met Defender, back-up en helpdesk — voor cloudwerkplekken, kioskapparaten en mobiele medewerkers.",
    },
    software: {
      serviceType: "Software License Reseller",
      category: "Software licensing and management",
      description:
        "Licentiebeheer voor zakelijke software: Microsoft 365, Adobe Creative Cloud en Autodesk. Activatie, toewijzing en beheer geregeld door Rinqel.",
    },
    hardware: {
      serviceType: "IT Hardware Supply and Installation",
      category: "IT hardware procurement and deployment",
      description:
        "Levering, configuratie en installatie van zakelijke hardware: laptops, desktops, monitoren, telefoons, tablets, printers, netwerkapparatuur en camerasystemen.",
    },
    "alles-in-een": {
      serviceType: "Managed IT Services (All-in-one)",
      category: "Managed IT services / Full-service provider",
      description:
        "Eén aanspreekpunt voor je complete IT: internet, netwerk, telefonie, mobiel, werkplekken, software en hardware — beheerd door Rinqel, met vaste prijs per maand.",
    },
  };

  // Per-slug SEO-title + zoekwoordrijke H1 (overrides het generieke service.title).
  // Titles ≤60 tekens, eindigend op "| Rinqel" — primaire term vooraan.
  const seoOverrides: Record<string, { title: string; description?: string; h1: string }> = {
    internet:      { title: "Zakelijk internet — glasvezel, VDSL & 5G-backup | Rinqel", h1: "Zakelijk internet voor bedrijven" },
    netwerken:     { title: "Netwerkbeheer voor bedrijven — WiFi & firewall | Rinqel", h1: "Netwerkbeheer voor bedrijven" },
    telefonie:     { title: "Zakelijke VoIP-telefonie — cloud telefooncentrale | Rinqel", h1: "Zakelijke telefonie via de cloud" },
    werkplek:      { title: "Digitale werkplek met Microsoft 365 | Rinqel", h1: "De digitale werkplek met Microsoft 365" },
    software:      { title: "Zakelijke software & licenties — Microsoft 365 | Rinqel", h1: "Zakelijke software & licenties" },
    hardware:      { title: "Zakelijke hardware — laptops, telefoons & meer | Rinqel", h1: "Zakelijke hardware, geleverd en geïnstalleerd" },
    "alles-in-een":{ title: "Alles-in-één IT — één aanspreekpunt | Rinqel", h1: "Alles-in-één IT met één aanspreekpunt" },
  };
  const override = slug ? seoOverrides[slug] : undefined;
  const pageTitle = override?.title ?? service.title;
  const pageH1 = override?.h1 ?? service.title;

  const meta = (slug && serviceMeta[slug]) || {
    serviceType: service.title,
    category: "Business IT services",
    description: service.longDescription,
  };

  // Build an OfferCatalog from listed items (only those with a € price)
  const priceRegex = /€\s?([0-9]+(?:[.,][0-9]{1,2})?)/;
  const offers = service.items
    .map((item) => {
      const match = item.price?.match(priceRegex);
      const offer: Record<string, unknown> = {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: item.name,
          description: item.description,
        },
      };
      if (match) {
        offer.price = match[1].replace(",", ".");
        offer.priceCurrency = "EUR";
        offer.priceSpecification = {
          "@type": "UnitPriceSpecification",
          price: match[1].replace(",", "."),
          priceCurrency: "EUR",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
        };
        offer.availability = "https://schema.org/InStock";
      }
      return offer;
    })
    .filter(Boolean);

  const serviceJsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://rinqel.nl/diensten/${service.slug}#service`,
    name: `${service.title} — Rinqel`,
    serviceType: meta.serviceType,
    category: meta.category,
    description: meta.description,
    url: `https://rinqel.nl/diensten/${service.slug}`,
    provider: { "@id": "https://rinqel.nl/#organization" },
    brand: { "@id": "https://rinqel.nl/#organization" },
    areaServed: {
      "@type": "Country",
      name: "Netherlands",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `https://rinqel.nl/diensten/${service.slug}`,
      servicePhone: "+31858223400",
      availableLanguage: ["nl", "en"],
    },
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Zakelijke klanten (MKB en zzp)",
    },
    
    ...(offers.length
      ? {
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${service.title} — diensten en abonnementen`,
            itemListElement: offers,
          },
        }
      : {}),
    ...(service.image
      ? { image: service.image }
      : {}),
  };

  const serviceFAQMap: Record<string, typeof internetFAQ | undefined> = {
    internet: internetFAQ,
    telefonie: telefonieFAQ,
    werkplek: werkplekFAQ,
  };
  const faqItems = slug ? serviceFAQMap[slug] : undefined;

  const jsonLd: Record<string, unknown>[] = [serviceJsonLd];
  if (faqItems) jsonLd.push(buildFAQSchema(faqItems));

  return (
    <Layout seoTitle={pageTitle} seoDescription={service.longDescription} seoJsonLd={jsonLd}>



      {hasFullHero ? (
        /* Full-width hero with background image — same look as homepage */
        <section className="relative min-h-[480px] overflow-hidden">
          <img
            src={service.image}
            alt={service.imageAlt}
            loading="eager" decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25" />
          <div className="container relative z-10 flex min-h-[480px] items-center py-20">
            <div className="max-w-2xl">
              <Link
                to="/"
                className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Terug naar overzicht
              </Link>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Maandelijks opzegbaar · Geen kleine lettertjes
                </span>
              </motion.div>

              <motion.div
                className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.05 }}
              >
                <Icon className="h-7 w-7 text-white" />
              </motion.div>

              <motion.h1
                className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-6xl"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                {pageH1.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-gradient">{pageH1.split(" ").slice(-1)}</span>
              </motion.h1>

              <motion.p
                className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {service.longDescription}
              </motion.p>

              <motion.div
                className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Altijd bereikbaar
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                  Dagelijks aanpasbaar
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                  99%+ uptime
                </span>
              </motion.div>
            </div>
          </div>
        </section>
      ) : (
        /* Subtle gradient hero — for services without image */
        <section className="bg-hero-gradient-subtle py-16 md:py-24">
          <div className="container">
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar overzicht
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-rinqel-blue-gradient">
                <Icon className="h-7 w-7 text-primary-foreground" />
              </div>
              <h1 className="text-3xl font-bold md:text-5xl">{pageH1}</h1>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground md:text-xl">
                {service.longDescription}
              </p>
            </motion.div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-24 bg-hero-gradient-subtle">
        <div className="container">
          <div className={`grid gap-6 ${displayItems.length <= 4 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
            {displayItems.map((item, index) => {
              const meta = itemMeta[item.name];
              const ItemIcon = meta?.icon;
              return (
                <motion.div
                  key={item.name}
                  className="flex flex-col rounded-xl border border-border bg-card shadow-card overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  {/* Colored top bar + icon */}
                  <div className={`relative flex items-center gap-3 px-6 py-4 ${meta?.gradient ?? "bg-hero-gradient"}`}>
                    <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full bg-white/10" />
                    {item.name === "Managed Werkplek" && (
                      <span className="absolute right-3 top-2.5 rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
                        Meest compleet
                      </span>
                    )}
                    {ItemIcon && (
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20">
                        <ItemIcon className="h-5 w-5 text-white" />
                      </div>
                    )}
                    <h3 className="text-base font-bold text-white leading-tight">{item.name}</h3>
                  </div>
                  {/* Body */}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                    <div className="mt-4 border-t border-border pt-4">
                      {item.price ? (
                        <div>
                          <span className="text-2xl font-bold text-gradient">{item.price}</span>
                          {item.priceNote && (
                            <span className="ml-1 text-sm text-muted-foreground">{item.priceNote}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Prijs op aanvraag</span>
                      )}
                    </div>
                    {itemLinks[item.name] && (
                      <Button variant="link" className="mt-2 justify-start px-0" asChild>
                        <Link to={itemLinks[item.name]}>
                          Meer informatie
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {slug === "werkplek" && (
            <motion.div
              className="mt-10 mx-auto max-w-4xl rounded-xl border border-border bg-card p-6 md:p-8 shadow-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-hero-gradient text-primary-foreground">
                  <RefreshCw className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold">Overstappen? Wij regelen alles.</h3>
              </div>

              <p className="text-sm leading-relaxed text-muted-foreground">
                Klant worden bij Rinqel betekent geen gedoe. Wij nemen je volledige IT-omgeving over — van e-mail en bestanden tot beveiliging en apparaatbeheer. 
                Jij werkt gewoon door, wij doen de rest. En ja, koffie is van ons. ☕
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                  <div>
                    <span className="text-sm font-semibold">Geen dataverlies</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Mail, agenda's, bestanden — alles verhuist veilig mee</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                  <div>
                    <span className="text-sm font-semibold">Gewoon doorwerken</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Computers worden doorgewisseld — inloggen en klaar</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                  <div>
                    <span className="text-sm font-semibold">Computer als nieuw</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Schone herinstallatie — beter voor prestaties én het milieu</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-lg bg-muted/40 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <span className="text-sm font-semibold">Eenmalig € 225,00 per werkplek</span>
                  <p className="text-xs text-muted-foreground mt-0.5">Groeit je team later? Nieuwe medewerkers erbij = geen extra kosten!</p>
                </div>
                <Button size="sm" asChild>
                  <Link to="/diensten/werkplek/overstap">
                    Bekijk het hele overstapproces
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          )}

          {slug === "hardware" && (
            <motion.div
              className="mt-10 mx-auto max-w-4xl rounded-xl border border-border bg-card p-6 md:p-8 shadow-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rinqel-green-gradient text-primary-foreground">
                  <Recycle className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold">Oude devices inruilen</h3>
              </div>

              <p className="text-sm leading-relaxed text-muted-foreground">
                Bij Rinqel spreken we vooraf een gegarandeerde inruilwaarde af voor je oude smartphones, tablets en computers. 
                Aan het einde van de looptijd verrekenen we dat bedrag op je nieuwe hardware. 
                Goed voor je portemonnee én het milieu. ♻️
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                  <div>
                    <span className="text-sm font-semibold">Gegarandeerde waarde</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Je weet vooraf wat je terugkrijgt — geen verrassingen</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
                  <Leaf className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                  <div>
                    <span className="text-sm font-semibold">Duurzaam & circulair</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Devices krijgen een tweede leven in de refurbished markt</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
                  <Shield className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                  <div>
                    <span className="text-sm font-semibold">Gecertificeerde datawiping</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Veilig gewist met datawiping-certificaat — geen datalekken</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-lg bg-muted/40 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <span className="text-sm font-semibold">Smartphones · Tablets · Computers</span>
                  <p className="text-xs text-muted-foreground mt-0.5">Verrekening bij aanschaf van nieuwe hardware — geen leasing, geen vooruitbetaling</p>
                </div>
                <Button size="sm" asChild>
                  <Link to="/diensten/hardware/inruilen">
                    Meer over inruilen
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          )}

          {slug === "internet" && <InternetContent />}
          {slug === "netwerken" && <NetworkContent />}
          {slug === "telefonie" && <TelefonieContent />}
          {slug === "werkplek" && <WerkplekContent />}



        </div>
      </section>
      {faqItems && <FAQ items={faqItems} />}
    </Layout>

  );
};

export default ServicePage;
